import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import Avatar3D from "@/components/Avatar3D";
import { Mic, MicOff, Loader2, Plus, Minus, X, Check, Send, ShoppingBag, Bell } from "lucide-react";

const API = `${window.location.origin}/api`;

// Category glow palette
const CAT_COLORS = [
  { border: '#f59e0b', glow: 'rgba(245,158,11,0.35)', bg: 'rgba(245,158,11,0.08)' },
  { border: '#ef4444', glow: 'rgba(239,68,68,0.35)', bg: 'rgba(239,68,68,0.08)' },
  { border: '#a855f7', glow: 'rgba(168,85,247,0.35)', bg: 'rgba(168,85,247,0.08)' },
  { border: '#3b82f6', glow: 'rgba(59,130,246,0.35)', bg: 'rgba(59,130,246,0.08)' },
  { border: '#22c55e', glow: 'rgba(34,197,94,0.35)', bg: 'rgba(34,197,94,0.08)' },
  { border: '#ec4899', glow: 'rgba(236,72,153,0.35)', bg: 'rgba(236,72,153,0.08)' },
];

// ─── Main Component ────────────────────────────────────────
export const CustomerMenu = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const masaFromUrl = searchParams.get("masa") || "";

  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  const [selectedItems, setSelectedItems] = useState([]);
  const [tableNumber, setTableNumber] = useState(masaFromUrl);
  const [orderNote, setOrderNote] = useState("");
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [callingWaiter, setCallingWaiter] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showWifiModal, setShowWifiModal] = useState(false);
  const [requestingBill, setRequestingBill] = useState(false);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [textInput, setTextInput] = useState("");

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recAudioCtxRef = useRef(null);
  const ttsContextRef = useRef(null);
  const currentSourceRef = useRef(null);
  const speakTimeoutRef = useRef(null);
  const isRecordingRef = useRef(false);
  const hasSpeechRef = useRef(false);
  const maxTimerRef = useRef(null);
  const greetedRef = useRef(false);
  const carouselRef = useRef(null);
  const catItemRefs = useRef({});

  const ensureAudioContext = useCallback(() => {
    if (!ttsContextRef.current) ttsContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (ttsContextRef.current.state === "suspended") ttsContextRef.current.resume();
    return ttsContextRef.current;
  }, []);

  const playTTS = useCallback((text) => {
    if (text.includes("MENU_REDIRECT")) return;
    setIsSpeaking(true);
    if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch {} currentSourceRef.current = null; }
    if (speakTimeoutRef.current) clearTimeout(speakTimeoutRef.current);
    speakTimeoutRef.current = setTimeout(() => setIsSpeaking(false), 30000);
    axios.post(`${API}/ai/tts`, { text }).then(async (res) => {
      if (!res.data?.audio) { clearTimeout(speakTimeoutRef.current); setIsSpeaking(false); return; }
      try {
        const binary = atob(res.data.audio);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const ctx = ttsContextRef.current || ensureAudioContext();
        if (ctx.state === "suspended") await ctx.resume();
        const audioBuffer = await ctx.decodeAudioData(bytes.buffer.slice(0));
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        const gainNode = ctx.createGain();
        gainNode.gain.value = 1.0;
        source.connect(gainNode);
        gainNode.connect(ctx.destination);
        source.onended = () => { clearTimeout(speakTimeoutRef.current); setIsSpeaking(false); currentSourceRef.current = null; };
        currentSourceRef.current = source;
        source.start();
      } catch {
        try {
          const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
          audio.volume = 1.0;
          audio.onended = () => { clearTimeout(speakTimeoutRef.current); setIsSpeaking(false); };
          await audio.play();
        } catch { clearTimeout(speakTimeoutRef.current); setIsSpeaking(false); }
      }
    }).catch(() => { clearTimeout(speakTimeoutRef.current); setIsSpeaking(false); });
  }, [ensureAudioContext]);

  const speak = useCallback((text) => {
    if (text.includes("MENU_REDIRECT")) { setAiMessage("Menüye bakabilirsiniz!"); return; }
    setAiMessage(text);
    setChatHistory((prev) => [...prev, { role: "assistant", content: text }]);
    playTTS(text);
  }, [playTTS]);

  // ─── Recording ─────────────────────────────────────────
  const startRecording = async () => {
    if (isRecordingRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream; audioChunksRef.current = []; hasSpeechRef.current = false; isRecordingRef.current = true; setIsRecording(true);
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const src = audioCtx.createMediaStreamSource(stream); const analyser = audioCtx.createAnalyser(); analyser.fftSize = 512; src.connect(analyser); recAudioCtxRef.current = audioCtx;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => { stream.getTracks().forEach((t) => t.stop()); try { audioCtx.close(); } catch {} if (audioChunksRef.current.length > 0) { const blob = new Blob(audioChunksRef.current, { type: mimeType }); if (blob.size > 500) await transcribeAudio(blob); } };
      mediaRecorderRef.current = recorder; recorder.start(250);
      const dataArr = new Uint8Array(analyser.frequencyBinCount); let silenceStart = null;
      const checkAudio = () => { if (!isRecordingRef.current) return; analyser.getByteFrequencyData(dataArr); const avg = dataArr.reduce((a, b) => a + b, 0) / dataArr.length; if (avg > 12) { hasSpeechRef.current = true; silenceStart = null; } else if (hasSpeechRef.current) { if (!silenceStart) silenceStart = Date.now(); else if (Date.now() - silenceStart > 1500) { stopRecording(); return; } } requestAnimationFrame(checkAudio); };
      requestAnimationFrame(checkAudio);
      maxTimerRef.current = setTimeout(() => { if (isRecordingRef.current) stopRecording(); }, 15000);
    } catch { toast.error("Mikrofon izni gerekli."); setIsRecording(false); isRecordingRef.current = false; }
  };
  const stopRecording = () => { isRecordingRef.current = false; setIsRecording(false); if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; } if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop(); };
  const handleMicPress = () => { ensureAudioContext(); if (isRecordingRef.current) { stopRecording(); return; } if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch {} currentSourceRef.current = null; } if (speakTimeoutRef.current) clearTimeout(speakTimeoutRef.current); setIsSpeaking(false); startRecording(); };
  const transcribeAudio = async (blob) => { setIsTranscribing(true); try { const fd = new FormData(); fd.append("file", blob, "audio.webm"); const res = await axios.post(`${API}/ai/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 15000 }); const text = res.data?.text?.trim(); if (text) handleUserInput(text); else toast.info("Ses algilanamadi, tekrar deneyin."); } catch { toast.error("Ses tanima basarisiz."); } finally { setIsTranscribing(false); } };

  const handleUserInput = async (text) => {
    if (!text.trim() || isProcessing) return; setIsProcessing(true);
    setChatHistory((prev) => [...prev, { role: "user", content: text }]);
    try {
      const res = await axios.post(`${API}/ai/chat/${slug}`, { message: text, conversation_history: chatHistory.slice(-6) });
      const response = res.data.response.replace(/\{[\s\S]*?\}/g, "").replace(/```[\s\S]*?```/g, "").trim();
      speak(response || "Bir sorun olustu.");
      if (res.data.order_data?.order_ready && res.data.order_data.items?.length > 0) {
        setSelectedItems(res.data.order_data.items.map((i) => { const mi = items.find((m) => m.id === i.item_id || m.name.toLowerCase() === i.name?.toLowerCase()); return { id: i.item_id || mi?.id || i.name, name: i.name, price: (i.price && i.price > 0) ? i.price : (mi?.price || 0), quantity: i.quantity || 1 }; }));
        if (res.data.order_data.table_number) setTableNumber(res.data.order_data.table_number);
        if (res.data.order_data.notes) setOrderNote(res.data.order_data.notes);
        setShowOrderModal(true);
      }
    } catch { speak("Bir sorun olustu, tekrar deneyin."); } finally { setIsProcessing(false); }
  };
  const sendTextMessage = () => { if (!textInput.trim()) return; ensureAudioContext(); handleUserInput(textInput.trim()); setTextInput(""); };

  // ─── Data ──────────────────────────────────────────────
  useEffect(() => { axios.get(`${API}/public/restaurant/${slug}`).then((res) => { setRestaurant(res.data.restaurant); setCategories(res.data.categories); setItems(res.data.items); setLoading(false); }).catch(() => { setError("Restoran bulunamadi"); setLoading(false); }); }, [slug]);
  useEffect(() => {
    if (restaurant && !greetedRef.current) {
      greetedRef.current = true;
      const g = "Hoş geldiniz, ne arzu edersiniz?";
      setAiMessage(g);
      setChatHistory([{ role: "assistant", content: g }]);
      
      let played = false;
      const cleanupListeners = () => {
        document.removeEventListener('click', onInteract);
        document.removeEventListener('touchstart', onInteract);
      };
      const onInteract = () => {
        if (!played) {
          played = true;
          ensureAudioContext();
          playTTS(g);
        }
        cleanupListeners();
      };
      
      // Try autoplay after short delay
      setTimeout(() => {
        try {
          const ctx = ensureAudioContext();
          if (ctx.state === 'running') {
            played = true;
            playTTS(g);
          }
        } catch {}
        // Only add interaction listeners if autoplay didn't work
        if (!played) {
          document.addEventListener('click', onInteract, { once: true });
          document.addEventListener('touchstart', onInteract, { once: true });
        }
      }, 500);
    }
  }, [restaurant, playTTS, ensureAudioContext]);
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      const tops = categories.filter(c => !c.parent_id);
      const visibleCats = tops.filter(c => items.some(i => i.category_id === c.id));
      if (visibleCats.length > 0) {
        const midIdx = Math.floor(visibleCats.length / 2);
        setActiveCategory(visibleCats[midIdx].id);
      }
    }
  }, [categories, activeCategory, items]);

  // Scroll the active category into center
  const scrollCatToCenter = useCallback((catId, behavior = 'smooth') => {
    const el = catItemRefs.current[catId];
    if (el) {
      el.scrollIntoView({ behavior, inline: 'center', block: 'nearest' });
    }
  }, []);

  // On first render, center the initial category
  useEffect(() => {
    if (activeCategory && carouselRef.current) {
      // Small delay to let DOM render
      const timer = setTimeout(() => scrollCatToCenter(activeCategory, 'auto'), 150);
      return () => clearTimeout(timer);
    }
  }, [activeCategory, scrollCatToCenter, categories]);
  useEffect(() => { return () => { if (isRecordingRef.current) stopRecording(); if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch {} } if (ttsContextRef.current) { try { ttsContextRef.current.close(); } catch {} } }; /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (!restaurant) return; [{ url: restaurant.custom_heading_font_url, name: restaurant.heading_font }, { url: restaurant.custom_subheading_font_url, name: restaurant.subheading_font }, { url: restaurant.custom_description_font_url, name: restaurant.description_font }].forEach(({ url, name }) => { if (url && name) { const f = new FontFace(name, `url(${url})`); f.load().then((l) => document.fonts.add(l)).catch(() => {}); } }); }, [restaurant]);

  const addItem = (item) => { setSelectedItems((prev) => { const e = prev.find((i) => i.id === item.id); if (e) return prev.map((i) => (i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i)); return [...prev, { id: item.id, name: item.name, price: item.price, quantity: 1 }]; }); toast.success(`${item.name} eklendi`); };
  const submitOrder = async () => { if (selectedItems.length === 0) return; setSubmitting(true); try { await axios.post(`${API}/public/orders/${slug}`, { items: selectedItems.map((i) => ({ item_id: i.id, name: i.name, quantity: i.quantity, price: i.price })), table_number: tableNumber || masaFromUrl || "Belirtilmedi", notes: orderNote }); speak("Siparişiniz alındı, afiyet olsun!"); toast.success("Sipariş gönderildi!"); setSelectedItems([]); setOrderNote(""); setShowOrderModal(false); } catch { speak("Sipariş gönderilemedi, tekrar deneyin."); } finally { setSubmitting(false); } };
  const callWaiter = async () => { setCallingWaiter(true); try { await axios.post(`${API}/public/waiter-call/${slug}`, { table_number: tableNumber || masaFromUrl || "Belirtilmedi" }); toast.success("Garson çağrıldı!"); playTTS("Garson çağrıldı, biraz bekleyin lütfen."); } catch { toast.error("Garson çağrılamadı"); } finally { setTimeout(() => setCallingWaiter(false), 5000); } };

  const requestBill = async (paymentType) => { setRequestingBill(true); setShowBillModal(false); try { await axios.post(`${API}/public/bill-request/${slug}`, { table_number: tableNumber || masaFromUrl || "Belirtilmedi", payment_type: paymentType }); toast.success(`Hesap istendi! (${paymentType === 'card' ? 'Kart' : 'Nakit'})`); playTTS("Hesabınız hazırlanıyor."); } catch { toast.error("Hesap istenemedi"); } finally { setTimeout(() => setRequestingBill(false), 5000); } };

  const features = restaurant?.features || { wifi_visible: true, google_review: true, bill_request: true, waiter_call: true, ai_assistant: true };

  const total = selectedItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const busy = isRecording || isTranscribing || isProcessing;
  const headingFont = restaurant?.heading_font || "Playfair Display";
  const descFont = restaurant?.description_font || "Inter";
  const pc = restaurant?.primary_color || "#c8956c";
  const sc = restaurant?.secondary_color || "#d4a574";
  const bg = restaurant?.background_color || "#faf6f1";

  // Derive full theme palette from restaurant colors
  const hexToRgb = (hex) => { const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16); return `${r},${g},${b}`; };
  const pcRgb = hexToRgb(pc);
  const scRgb = hexToRgb(sc);
  const theme = {
    bg, pc, sc,
    bgGradient: `linear-gradient(160deg, ${bg} 0%, ${bg}f0 50%, ${bg}e0 100%)`,
    cardBg: 'rgba(255,255,255,0.72)',
    headerBg: `rgba(255,255,255,0.55)`,
    glow: `rgba(${pcRgb},0.3)`,
    glowSoft: `rgba(${pcRgb},0.08)`,
    accent: `rgba(${scRgb},0.15)`,
    border: `rgba(${pcRgb},0.12)`,
    micBg: `linear-gradient(135deg, ${pc}, ${sc})`,
    btnBg: `linear-gradient(135deg, ${pc}, ${sc})`,
    btnShadow: `0 6px 25px rgba(${pcRgb},0.3)`,
  };

  // Subcategory support
  const mainCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (catId) => categories.filter(c => c.parent_id === catId);
  const activeSubs = activeCategory ? getSubcategories(activeCategory) : [];

  const activeCatItems = items.filter(i => {
    if (i.category_id !== activeCategory) return false;
    if (activeSubcategory) {
      // Show only items assigned to this specific subcategory
      return i.subcategory_id === activeSubcategory;
    }
    return true;
  });
  // If no subcategory selected, show all items of the category
  const allCatItems = activeSubcategory ? activeCatItems : items.filter(i => i.category_id === activeCategory);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bgGradient }}>
      <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', damping: 15, stiffness: 100 }} className="flex flex-col items-center gap-5">
        {restaurant?.logo_url ? (
          <div className="relative">
            {/* Outer pulse ring */}
            <motion.div className="absolute inset-[-16px] rounded-3xl" animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.05, 0.15] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              style={{ background: `radial-gradient(circle, rgba(${pcRgb},0.15), transparent 70%)` }} />
            {/* Inner glow ring */}
            <motion.div className="absolute inset-[-6px] rounded-3xl" animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{ border: `2px solid ${pc}44`, boxShadow: `0 0 30px ${theme.glow}` }} />
            {/* Logo with scale + rotate animation */}
            <motion.img src={restaurant.logo_url} alt="" className="w-24 h-24 rounded-3xl object-cover relative z-10"
              initial={{ rotate: -10, scale: 0.6, opacity: 0 }}
              animate={{ rotate: 0, scale: [1, 1.06, 1], opacity: 1 }}
              transition={{ rotate: { duration: 0.6, ease: 'easeOut' }, scale: { duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }, opacity: { duration: 0.5 } }}
              style={{ boxShadow: `0 12px 40px ${theme.glow}, 0 4px 16px rgba(0,0,0,0.1)` }} />
          </div>
        ) : (
          <motion.div className="w-24 h-24 rounded-3xl flex items-center justify-center" animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 2, repeat: Infinity }} style={{ background: theme.glowSoft, border: `2px solid ${theme.border}` }}>
            <span className="text-3xl font-bold" style={{ color: pc }}>{restaurant?.name?.[0] || 'M'}</span>
          </motion.div>
        )}
        {/* Animated loading bar */}
        <motion.div className="h-1 rounded-full" initial={{ width: 20, opacity: 0 }} animate={{ width: [30, 70, 30], opacity: 1 }} transition={{ width: { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }, opacity: { duration: 0.3 } }} style={{ background: theme.btnBg }} />
        {/* Restaurant name fade-in */}
        {restaurant?.name && (
          <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="text-sm font-medium" style={{ color: `${pc}aa`, fontFamily: `'${headingFont}', serif` }}>
            {restaurant.name}
          </motion.p>
        )}
      </motion.div>
    </div>
  );
  if (error) return <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bgGradient }}><p className="text-gray-400">{error}</p></div>;

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="customer-menu-page">

      {/* ── Premium Background (theme-aware) ── */}
      <div className="fixed inset-0 z-0" style={{ background: theme.bgGradient }}>
        <div className="absolute w-[500px] h-[500px] rounded-full opacity-20" style={{ top: '-15%', right: '-10%', background: `radial-gradient(circle, rgba(${pcRgb},0.1), transparent 70%)` }} />
        <div className="absolute w-[400px] h-[400px] rounded-full opacity-15" style={{ bottom: '5%', left: '-10%', background: `radial-gradient(circle, rgba(${scRgb},0.08), transparent 70%)` }} />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 flex flex-col min-h-screen">

        {/* ── Header (scrolls with page, behind avatar) ── */}
        <header className="relative z-40 px-4 py-2.5" style={{
          background: theme.headerBg,
          backdropFilter: 'blur(20px) saturate(1.3)',
          WebkitBackdropFilter: 'blur(20px) saturate(1.3)',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {restaurant?.logo_url ? (
                <img src={restaurant.logo_url} alt="" className="w-10 h-10 rounded-2xl object-cover" style={{ border: `1.5px solid ${theme.border}`, boxShadow: `0 2px 10px ${theme.glow}` }} data-testid="restaurant-logo" />
              ) : (
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: theme.glowSoft, border: `1.5px solid ${theme.border}` }}>
                  <span className="font-bold text-lg" style={{ color: pc }}>{restaurant?.name?.[0]}</span>
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-gray-800 font-bold text-[17px] truncate" style={{ fontFamily: `'${headingFont}', serif` }} data-testid="restaurant-name">{restaurant?.name}</h1>
              </div>
            </div>
            {tableNumber && (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full" style={{ background: theme.glowSoft, border: `1px solid ${theme.border}` }}>
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: pc, boxShadow: `0 0 6px ${theme.glow}` }} />
                <p className="text-gray-500 text-xs font-medium">Masa {tableNumber}</p>
              </div>
            )}
          </div>
        </header>

        {/* ── AI Assistant Section ── */}
        {features.ai_assistant && (
        <div className="relative px-5 pb-0" style={{ marginTop: -24, zIndex: 45 }}>
          <div className="flex flex-col items-center" data-testid="ai-assistant-section">
            {/* Avatar - head pops above, body in circle, NO bottom leak */}
            <div className="relative" style={{ width: 76, height: 110, marginBottom: -38, zIndex: 50 }}>
              {/* Outer glow pulse */}
              <motion.div className="absolute rounded-full"
                animate={isSpeaking ? { scale: [1, 1.1, 1], opacity: [0.3, 0.08, 0.3] } : isRecording ? { scale: [1, 1.12, 1], opacity: [0.35, 0.1, 0.35] } : { scale: [1, 1.05, 1], opacity: [0.15, 0.06, 0.15] }}
                transition={{ duration: isSpeaking ? 0.8 : isRecording ? 1 : 3, repeat: Infinity }}
                style={{ bottom: 0, left: -8, right: -8, height: 88, background: isRecording ? 'radial-gradient(circle, rgba(239,68,68,0.25), transparent 70%)' : `radial-gradient(circle, rgba(${pcRgb},0.2), transparent 70%)` }} />
              {/* Conic gradient ring - positioned at bottom (circle area) */}
              <div className="absolute rounded-full" style={{
                bottom: -3, left: -3, right: -3, height: 78,
                background: `conic-gradient(from 180deg, ${pc}, ${sc}, ${pc}cc, ${sc}, ${pc})`,
                WebkitMask: 'radial-gradient(circle, transparent 55%, black 57%)',
                mask: 'radial-gradient(circle, transparent 55%, black 57%)',
                boxShadow: `0 4px 20px ${theme.glow}`,
              }} />
              {/* Circle background - visual frame at bottom */}
              <div className="absolute rounded-full" style={{
                bottom: 2, left: 2, right: 2, height: 68,
                background: `linear-gradient(180deg, ${bg}, ${bg}ee)`,
                boxShadow: `inset 0 2px 8px rgba(0,0,0,0.04)`,
              }} />
              {/* Avatar - head free above, clipped only at circle bottom */}
              <div className="absolute" style={{
                bottom: 0, left: '50%', width: 120, height: 140,
                transform: 'translateX(-50%)',
                clipPath: 'inset(-999px -22px 0px -22px)',
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))',
              }}>
                <Avatar3D isSpeaking={isSpeaking} isListening={isRecording} primaryColor={pc} />
              </div>
              {/* Status indicator dot */}
              {isRecording && <motion.div className="absolute w-3.5 h-3.5 rounded-full border-2" style={{ bottom: 2, right: 2, borderColor: bg, background: '#ef4444', zIndex: 60 }} animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 0.7, repeat: Infinity }} />}
              {isSpeaking && <motion.div className="absolute w-3.5 h-3.5 rounded-full border-2" style={{ bottom: 2, right: 2, borderColor: bg, background: '#22c55e', zIndex: 60 }} animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.5, repeat: Infinity }} />}
            </div>
            {/* AI Card - avatar tilts into it */}
            <div className="w-full rounded-[18px] pt-10 pb-2.5 px-3.5" style={{ background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: `1px solid ${theme.border}`, boxShadow: '0 6px 24px rgba(0,0,0,0.04)' }}>
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <div className={`w-1.5 h-1.5 rounded-full ${isRecording ? 'bg-red-400 animate-pulse' : isSpeaking ? 'bg-emerald-400 animate-pulse' : busy ? 'bg-amber-400 animate-pulse' : ''}`} style={!isRecording && !isSpeaking && !busy ? { background: pc } : {}} />
                <span className="text-[9px] font-medium tracking-wider uppercase" style={{ color: isRecording ? '#ef4444' : isSpeaking ? '#22c55e' : '#9ca3af' }}>
                  {isRecording ? "Dinliyorum" : isTranscribing ? "Anliyorum" : isSpeaking ? "Konusuyor" : isProcessing ? "Dusunuyor" : "Hazir"}
                </span>
              </div>
              <p className="text-center text-gray-600 text-[13px] leading-relaxed mb-2.5 px-1" data-testid="ai-message-bubble" style={{ fontFamily: `'${headingFont}', serif` }}>
                {isTranscribing ? "Anliyorum..." : isProcessing ? "Dusunuyorum..." : aiMessage || "Hos geldiniz, ne arzu edersiniz?"}
              </p>
              {/* Centered mic button with breathing rings */}
              <div className="flex flex-col items-center mb-2.5">
                <div className="relative">
                  <motion.div className="absolute inset-[-12px] rounded-full" style={{ border: isRecording ? '1.5px solid rgba(239,68,68,0.2)' : `1.5px solid rgba(${pcRgb},0.12)` }} animate={isRecording ? { scale: [1, 1.1, 1] } : { scale: [1, 1.06, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                  <motion.div className="absolute inset-[-5px] rounded-full" style={{ border: isRecording ? '2px solid rgba(239,68,68,0.3)' : `2px solid rgba(${pcRgb},0.18)` }} animate={isRecording ? { scale: [1, 1.06, 1] } : { scale: [1, 1.03, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                  <button onClick={handleMicPress} disabled={isTranscribing || isProcessing} className="relative z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50"
                    style={isRecording ? { background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 6px 20px rgba(239,68,68,0.3)' } : { background: theme.micBg, boxShadow: theme.btnShadow }} data-testid="mic-button">
                    {isRecording ? <MicOff className="w-[18px] h-[18px] text-white" /> : <Mic className="w-[18px] h-[18px] text-white" />}
                  </button>
                </div>
              </div>
              {/* Text input row */}
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <Input value={textInput} onChange={(e) => setTextInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendTextMessage()} placeholder="Yazarak sipariş verin..." disabled={isProcessing || isRecording}
                    className="h-9 rounded-full text-xs text-gray-600 placeholder:text-gray-300 border-0 pr-9 focus-visible:ring-1" style={{ background: 'rgba(255,255,255,0.5)', border: `1px solid ${theme.border}` }} data-testid="text-input" />
                  <button onClick={sendTextMessage} disabled={!textInput.trim() || isProcessing} className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full h-7 w-7 flex items-center justify-center transition-all duration-200 hover:scale-110 disabled:opacity-20" style={{ background: theme.btnBg }} data-testid="send-text-btn">
                    <Send className="w-3 h-3 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* ── Category Orbit: Elliptical Carousel ── */}
        <div className="relative pb-4 pt-1 overflow-visible">
          {/* Elliptical orbit rings (SVG behind cards) */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 430 200" preserveAspectRatio="none" style={{ zIndex: 0 }}>
            <ellipse cx="215" cy="110" rx="200" ry="70" fill="none" stroke="url(#orbitGrad1)" strokeWidth="1.2" opacity="0.25" />
            <ellipse cx="215" cy="110" rx="170" ry="55" fill="none" stroke="url(#orbitGrad2)" strokeWidth="0.8" opacity="0.15" />
            <defs>
              <linearGradient id="orbitGrad1" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.4" />
                <stop offset="50%" stopColor="#a855f7" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="orbitGrad2" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.2" />
                <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#a855f7" stopOpacity="0.2" />
              </linearGradient>
            </defs>
          </svg>

          {/* Scrollable category row */}
          <div ref={carouselRef} className="relative z-10 flex items-center gap-4 overflow-x-auto scrollbar-hide px-4 py-3"
            style={{ scrollbarWidth: 'none', scrollSnapType: 'x mandatory', perspective: '800px' }}
            data-testid="category-carousel"
          >
            <div className="flex-shrink-0 w-[calc(50vw-85px)]" />
            {mainCategories.map((cat, idx) => {
              const catItems = items.filter(i => i.category_id === cat.id);
              if (catItems.length === 0) return null;
              const isActive = activeCategory === cat.id;
              const totalInCat = catItems.reduce((s, i) => s + (selectedItems.find((o) => o.id === i.id)?.quantity || 0), 0);

              return (
                <motion.button key={cat.id}
                  ref={el => { catItemRefs.current[cat.id] = el; }}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setActiveSubcategory(null);
                    scrollCatToCenter(cat.id);
                    if (navigator.vibrate) navigator.vibrate(15);
                  }}
                  whileTap={{ scale: 0.92 }}
                  animate={isActive ? { scale: 1, y: 0 } : { scale: 0.85, y: 8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  className="flex-shrink-0 relative overflow-visible"
                  style={{ width: 130, height: 165, scrollSnapAlign: 'center', transformStyle: 'preserve-3d' }}
                  data-testid={`category-toggle-${cat.id}`}
                >
                  {isActive && (
                    <motion.div className="absolute inset-[-6px] rounded-[26px]"
                      animate={{ opacity: [0.4, 0.7, 0.4] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      style={{ boxShadow: `0 0 30px ${theme.glow}, 0 0 60px ${pc}33`, borderRadius: 26 }} />
                  )}
                  <div className="absolute inset-0 rounded-[24px] overflow-hidden transition-all duration-300" style={{
                    border: isActive ? `2px solid ${pc}66` : '1.5px solid rgba(0,0,0,0.04)',
                    boxShadow: isActive ? `0 10px 35px rgba(0,0,0,0.12), 0 0 25px ${theme.glow}, inset 0 1px 0 rgba(255,255,255,0.6)` : '0 4px 18px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.5)',
                  }}>
                    {cat.image_url ? (
                      <>
                        <img src={cat.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0" style={{ background: isActive ? 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.6) 100%)' : 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.7) 100%)' }} />
                      </>
                    ) : (
                      <div className="absolute inset-0" style={{ background: isActive ? `linear-gradient(160deg, rgba(255,255,255,0.9), ${theme.glowSoft})` : 'linear-gradient(160deg, rgba(255,255,255,0.85), rgba(248,246,255,0.95))' }}>
                        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-16 h-16 rounded-full" style={{ background: `radial-gradient(circle, ${pc}12, transparent)`, border: `1px solid ${pc}10` }} />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 px-3 pb-3 text-center">
                      <p className="font-bold text-[15px] leading-tight drop-shadow-lg" style={{ fontFamily: `'${headingFont}', serif`, color: cat.image_url ? '#fff' : '#374151', textShadow: cat.image_url ? '0 2px 8px rgba(0,0,0,0.7)' : 'none' }}>{cat.name}</p>
                      <p className="text-[11px] mt-0.5" style={{ color: cat.image_url ? 'rgba(255,255,255,0.65)' : '#9ca3af' }}>{catItems.length} urun</p>
                    </div>
                  </div>
                  {totalInCat > 0 && (
                    <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ background: pc, boxShadow: `0 0 12px ${theme.glow}` }}>{totalInCat}</div>
                  )}
                  {isActive && (
                    <motion.div layoutId="catGlow" className="absolute -bottom-2 left-[15%] right-[15%] h-[3px] rounded-full"
                      style={{ background: `linear-gradient(90deg, transparent, ${pc}, transparent)`, boxShadow: `0 0 10px ${pc}88` }} />
                  )}
                </motion.button>
              );
            })}
            {/* Right spacer */}
            <div className="flex-shrink-0 w-[calc(50vw-85px)]" />
          </div>
        </div>

        {/* ── Active Category Items ── */}
        <div className="flex-1 px-4 pb-36" data-testid="menu-section">
          <AnimatePresence mode="wait">
            {activeCategory && (
              <motion.div key={`${activeCategory}-${activeSubcategory}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-gray-700 font-bold text-lg" style={{ fontFamily: `'${headingFont}', serif` }}>{mainCategories.find(c => c.id === activeCategory)?.name}</h2>
                  <span className="text-gray-400 text-xs font-medium tracking-wider uppercase">{allCatItems.length} urun</span>
                </div>
                {/* Subcategory tabs */}
                {activeSubs.length > 0 && (
                  <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide" data-testid="subcategory-tabs">
                    <button onClick={() => setActiveSubcategory(null)}
                      className="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all"
                      style={{ background: !activeSubcategory ? pc : 'rgba(0,0,0,0.03)', color: !activeSubcategory ? '#fff' : '#6b7280', border: `1px solid ${!activeSubcategory ? pc : 'rgba(0,0,0,0.06)'}` }}>
                      Tümü
                    </button>
                    {activeSubs.map(sub => (
                      <button key={sub.id} onClick={() => setActiveSubcategory(sub.id)}
                        className="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all"
                        style={{ background: activeSubcategory === sub.id ? pc : 'rgba(0,0,0,0.03)', color: activeSubcategory === sub.id ? '#fff' : '#6b7280', border: `1px solid ${activeSubcategory === sub.id ? pc : 'rgba(0,0,0,0.06)'}` }}
                        data-testid={`subcategory-${sub.id}`}>
                        {sub.name}
                      </button>
                    ))}
                  </div>
                )}
                {allCatItems.length > 0 ? (
                <div className="space-y-3" data-testid={`category-${activeCategory}`}>
                  {allCatItems.map((item, i) => {
                    const inOrder = selectedItems.find(o => o.id === item.id);
                    return (
                      <motion.div key={item.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05, type: 'spring', stiffness: 200 }}
                        className="relative rounded-[20px] overflow-hidden transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                        onClick={() => setSelectedItem(item)}
                        style={{
                          background: theme.cardBg,
                          backdropFilter: 'blur(16px)',
                          WebkitBackdropFilter: 'blur(16px)',
                          border: inOrder ? `1.5px solid ${pc}44` : `1px solid ${theme.border}`,
                          boxShadow: inOrder ? `0 0 20px ${theme.glow}, 0 8px 30px rgba(0,0,0,0.06)` : '0 2px 16px rgba(0,0,0,0.04)',
                        }}
                        data-testid={`menu-item-${item.id}`}>
                        <div className="flex items-stretch" style={{ minHeight: 110 }}>
                          {item.image_url ? (
                            <div className="relative w-[120px] flex-shrink-0 overflow-hidden" style={{ background: `${bg}f0` }}>
                              <img src={item.image_url} alt="" className="absolute inset-0 w-full h-full object-contain" />
                              <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent 60%, rgba(255,255,255,0.3) 100%)' }} />
                              {inOrder && <div className="absolute top-2.5 left-2.5 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: pc, boxShadow: `0 2px 8px ${theme.glow}` }}>{inOrder.quantity}x</div>}
                            </div>
                          ) : (
                            <div className="relative w-[120px] flex-shrink-0 flex items-center justify-center" style={{ background: `linear-gradient(160deg, ${bg}f8, ${theme.glowSoft})` }}>
                              <div className="w-14 h-14 rounded-full" style={{ background: `${pc}08`, border: `1.5px solid ${pc}12` }} />
                              {inOrder && <div className="absolute top-2.5 left-2.5 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: pc, boxShadow: `0 2px 8px ${theme.glow}` }}>{inOrder.quantity}x</div>}
                            </div>
                          )}
                          <div className="flex-1 flex flex-col justify-between p-3.5 min-w-0">
                            <div>
                              <p className="font-bold text-gray-800 text-[15px] leading-snug" style={{ fontFamily: `'${headingFont}', serif` }}>{item.name}</p>
                              {item.description && <p className="text-[12px] text-gray-400 mt-1 line-clamp-2 leading-relaxed" style={{ fontFamily: `'${descFont}', sans-serif` }}>{item.description}</p>}
                            </div>
                            <div className="flex items-center justify-between mt-2.5">
                              <p className="font-bold text-base" style={{ color: pc }}>{item.price.toFixed(2)} <span className="text-[11px] font-medium text-gray-400">TL</span></p>
                              <button onClick={(e) => { e.stopPropagation(); addItem(item); }}
                                className="rounded-full h-9 w-9 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-90"
                                style={{ background: theme.btnBg, boxShadow: theme.btnShadow }}
                                data-testid={`add-item-${item.id}`}>
                                <Plus className="w-4 h-4 text-white" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="subcategory-empty">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: theme.glowSoft, border: `1.5px solid ${theme.border}` }}>
                      <ShoppingBag className="w-6 h-6" style={{ color: `${pc}66` }} />
                    </div>
                    <p className="text-gray-400 text-sm text-center">Bu kategoride henuz urun eklenmemis</p>
                    <button onClick={() => setActiveSubcategory(null)} className="px-4 py-2 rounded-full text-xs font-semibold transition-all hover:scale-105"
                      style={{ background: theme.glowSoft, color: pc, border: `1px solid ${theme.border}` }}>
                      Tum urunleri goster
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          {mainCategories.length === 0 && <p className="text-gray-300 text-center py-12 text-sm">Menu henuz eklenmemis</p>}
        </div>
      </div>

      {/* ── Order Modal (Light Glass) ── */}
      <AnimatePresence>
        {showOrderModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(8px)' }} onClick={() => setShowOrderModal(false)} data-testid="order-modal-overlay">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }} className="w-full rounded-t-[28px] max-h-[88vh] overflow-hidden flex flex-col" style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px)', border: '1px solid rgba(168,85,247,0.1)', borderBottom: 'none', boxShadow: '0 -8px 40px rgba(0,0,0,0.1), 0 0 60px rgba(168,85,247,0.04)' }} onClick={(e) => e.stopPropagation()} data-testid="order-modal">
              <div className="px-5 pt-3 pb-4" style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ background: 'rgba(0,0,0,0.1)' }} />
                <div className="flex justify-between items-center">
                  <div><h2 className="text-xl font-bold text-gray-800">Siparişiniz</h2><p className="text-xs text-gray-400 mt-0.5">{selectedItems.length} urun{tableNumber ? ` · Masa ${tableNumber}` : ""}</p></div>
                  <button onClick={() => setShowOrderModal(false)} className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110" style={{ background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)' }} data-testid="close-order-modal"><X className="w-4 h-4 text-gray-400" /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-3">
                {selectedItems.length === 0 ? <div className="flex flex-col items-center justify-center py-12 gap-2"><ShoppingBag className="w-10 h-10 text-gray-200" /><p className="text-gray-300 text-sm">Sepetiniz bos</p></div> : (
                  <div className="space-y-2.5">
                    {selectedItems.map((item, idx) => (
                      <motion.div key={item.id} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }} className="rounded-2xl p-3.5 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }} data-testid={`order-item-${item.id}`}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)' }}><span className="text-indigo-500 font-bold text-sm">{item.quantity}x</span></div>
                        <div className="flex-1 min-w-0"><p className="font-semibold text-sm text-gray-800 truncate">{item.name}</p><p className="text-xs mt-0.5" style={{ color: pc }}>{(item.price * item.quantity).toFixed(2)} TL</p></div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setSelectedItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, quantity: Math.max(0, i.quantity - 1) } : i)).filter((i) => i.quantity > 0))} className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:scale-110" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)' }} data-testid={`decrease-${item.id}`}><Minus className="w-3.5 h-3.5 text-gray-400" /></button>
                          <span className="w-5 text-center text-sm font-bold text-gray-800">{item.quantity}</span>
                          <button onClick={() => setSelectedItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i)))} className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:scale-110" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 3px 10px rgba(99,102,241,0.2)' }} data-testid={`increase-${item.id}`}><Plus className="w-3.5 h-3.5 text-white" /></button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
                {selectedItems.length > 0 && <div className="mt-4"><Textarea value={orderNote} onChange={(e) => setOrderNote(e.target.value)} placeholder="Sipariş notunuz (opsiyonel)..." rows={2} className="text-sm rounded-2xl text-gray-700 placeholder:text-gray-300 border-0 focus-visible:ring-1 focus-visible:ring-purple-300" style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.04)' }} data-testid="order-note-input" /></div>}
              </div>
              {selectedItems.length > 0 && (
                <div className="px-5 pb-6 pt-3" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                  <div className="flex justify-between items-center mb-3"><span className="text-sm text-gray-400">Toplam</span><span className="text-2xl font-bold text-gray-800">{total.toFixed(2)} <span className="text-sm font-normal text-gray-400">TL</span></span></div>
                  <button onClick={submitOrder} disabled={submitting} className="w-full rounded-2xl py-4 text-base font-semibold text-white flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50" style={{ background: theme.btnBg, boxShadow: theme.btnShadow }} data-testid="submit-order-btn">
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}{submitting ? "Gönderiliyor..." : "Siparişi Onayla"}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Premium Bottom Navigation Bar ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40" data-testid="bottom-nav">
        {/* Order CTA - above bottom bar */}
        <AnimatePresence>
          {selectedItems.length > 0 && (
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }} className="flex justify-center px-4 mb-2">
              <button onClick={() => setShowOrderModal(true)}
                className="flex items-center gap-2.5 px-6 py-3.5 rounded-full text-sm font-bold text-white transition-all hover:scale-[1.03] active:scale-[0.97]"
                style={{ background: theme.btnBg, boxShadow: `0 8px 30px ${theme.glow}, 0 4px 12px rgba(0,0,0,0.1)` }}
                data-testid="open-order-btn">
                <ShoppingBag className="w-4.5 h-4.5" />
                <span>Siparişi Tamamla</span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(255,255,255,0.25)' }}>{selectedItems.reduce((s,i) => s+i.quantity, 0)} · {total.toFixed(0)} TL</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom action bar */}
        <div className="px-3 pb-3 pt-2" style={{
          background: 'rgba(255,255,255,0.45)',
          backdropFilter: 'blur(24px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
          borderTop: `1px solid ${theme.border}`,
        }}>
          <div className="flex items-center justify-around max-w-sm mx-auto">
            {features.waiter_call && (
              <button onClick={callWaiter} disabled={callingWaiter} className="flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all active:scale-90 disabled:opacity-40" data-testid="call-waiter-btn">
                <Bell className="w-5 h-5" style={{ color: callingWaiter ? '#10b981' : pc }} />
                <span className="text-[10px] font-medium" style={{ color: callingWaiter ? '#10b981' : '#6b7280' }}>{callingWaiter ? "Çağrıldı" : "Garson"}</span>
              </button>
            )}
            {features.bill_request && (
              <button onClick={() => setShowBillModal(true)} disabled={requestingBill} className="flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all active:scale-90 disabled:opacity-40" data-testid="request-bill-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={requestingBill ? '#10b981' : pc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M7 15h0M2 9.5h20"/></svg>
                <span className="text-[10px] font-medium" style={{ color: requestingBill ? '#10b981' : '#6b7280' }}>{requestingBill ? "İstendi" : "Hesap"}</span>
              </button>
            )}
            {features.wifi_visible && (
              <button onClick={() => { if (restaurant?.wifi_password) { setShowWifiModal(true); } else { toast.info("WiFi bilgisi henüz ayarlanmamış"); } }} className="flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all active:scale-90" data-testid="wifi-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={pc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13a10 10 0 0 1 14 0"/><path d="M8.5 16.5a5 5 0 0 1 7 0"/><path d="M2 8.82a15 15 0 0 1 20 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
                <span className="text-[10px] font-medium text-gray-500">WiFi</span>
              </button>
            )}
            {features.google_review && (
              restaurant?.google_business_url ? (
                <a href={restaurant.google_business_url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all active:scale-90" data-testid="google-review-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={pc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <span className="text-[10px] font-medium text-gray-500">Puanla</span>
                </a>
              ) : (
                <button onClick={() => toast.info("Google Business URL henüz ayarlanmamış")} className="flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all active:scale-90" data-testid="google-review-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={pc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <span className="text-[10px] font-medium text-gray-500">Puanla</span>
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* ── WiFi Modal ── */}
      <AnimatePresence>
        {showWifiModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(6px)' }} onClick={() => setShowWifiModal(false)} data-testid="wifi-modal-overlay">
            <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }} transition={{ type: 'spring', damping: 20 }}
              className="rounded-3xl p-6 text-center max-w-sm w-full" style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()} data-testid="wifi-modal">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.1)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13a10 10 0 0 1 14 0"/><path d="M8.5 16.5a5 5 0 0 1 7 0"/><path d="M2 8.82a15 15 0 0 1 20 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">WiFi Şifresi</h3>
              <p className="text-gray-400 text-xs mb-4">{restaurant?.name}</p>
              <div className="rounded-2xl p-4 mb-4 select-all" style={{ background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)' }}>
                <p className="text-xl font-mono font-bold text-gray-800 tracking-wider" data-testid="wifi-password-text">{restaurant?.wifi_password}</p>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(restaurant?.wifi_password || ''); toast.success("Kopyalandı!"); setShowWifiModal(false); }}
                className="w-full py-3 rounded-2xl text-sm font-semibold text-white transition-all hover:scale-[1.02]" style={{ background: theme.btnBg, boxShadow: theme.btnShadow }}>
                Kopyala
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Bill Payment Type Modal ── */}
      <AnimatePresence>
        {showBillModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(6px)' }} onClick={() => setShowBillModal(false)} data-testid="bill-modal-overlay">
            <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }} transition={{ type: 'spring', damping: 20 }}
              className="rounded-3xl p-6 text-center max-w-sm w-full" style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()} data-testid="bill-modal">
              <h3 className="text-lg font-bold text-gray-800 mb-1">Hesap İste</h3>
              <p className="text-gray-400 text-xs mb-5">Ödeme yönteminizi seçin</p>
              <div className="flex gap-3">
                <button onClick={() => requestBill('card')} className="flex-1 flex flex-col items-center gap-2 py-5 rounded-2xl transition-all hover:scale-[1.03] active:scale-95" style={{ background: theme.glowSoft, border: `1.5px solid ${theme.border}` }} data-testid="bill-card-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={pc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M7 15h0M2 9.5h20"/></svg>
                  <span className="text-sm font-semibold text-gray-700">Kart</span>
                </button>
                <button onClick={() => requestBill('cash')} className="flex-1 flex flex-col items-center gap-2 py-5 rounded-2xl transition-all hover:scale-[1.03] active:scale-95" style={{ background: theme.glowSoft, border: `1.5px solid ${theme.border}` }} data-testid="bill-cash-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={pc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  <span className="text-sm font-semibold text-gray-700">Nakit</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Item Detail Modal ── */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(6px)' }} onClick={() => setSelectedItem(null)} data-testid="item-detail-overlay">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="w-full rounded-t-[28px] max-h-[85vh] overflow-hidden flex flex-col" style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', boxShadow: '0 -8px 40px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()} data-testid="item-detail-modal">
              {/* Drag handle */}
              <div className="w-10 h-1 rounded-full mx-auto mt-3 mb-2" style={{ background: 'rgba(0,0,0,0.1)' }} />
              {/* Image */}
              {selectedItem.image_url && (
                <div className="relative w-full h-52 flex-shrink-0" style={{ background: `${bg}f0` }}>
                  <img src={selectedItem.image_url} alt={selectedItem.name} className="w-full h-full object-contain" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(255,255,255,0.6) 100%)' }} />
                </div>
              )}
              {/* Content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <h2 className="text-xl font-bold text-gray-800 mb-1" style={{ fontFamily: `'${headingFont}', serif` }} data-testid="item-detail-name">{selectedItem.name}</h2>
                {selectedItem.description && <p className="text-sm text-gray-500 mb-3" style={{ fontFamily: `'${descFont}', sans-serif` }}>{selectedItem.description}</p>}
                {selectedItem.long_description && (
                  <div className="rounded-2xl p-4 mb-4" style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line" style={{ fontFamily: `'${descFont}', sans-serif` }} data-testid="item-detail-long-desc">{selectedItem.long_description}</p>
                  </div>
                )}
                <p className="text-2xl font-bold mb-4" style={{ color: pc }}>{selectedItem.price.toFixed(2)} <span className="text-sm font-normal text-gray-400">TL</span></p>
              </div>
              {/* Add button */}
              <div className="px-5 pb-6 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                <button onClick={() => { addItem(selectedItem); setSelectedItem(null); }}
                  className="w-full py-4 rounded-2xl text-base font-semibold text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{ background: theme.btnBg, boxShadow: theme.btnShadow }}
                  data-testid="item-detail-add-btn">
                  <Plus className="w-5 h-5" /> Sepete Ekle
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
